import { useCallback, useEffect, useState } from 'react';
import type { ReservationsDTO } from '../model/ReservationsDTO';
import type { AxiosError } from 'axios';
import deleteRegistration from '../api/deleteRegistration';
import fetchAuthenticatedFromBackend from '../api/fetchAuthenticatedFromBackend';
import { useAuth } from '../context/AuthContext';

export default function useReservations(id: string) {
  const [reservations, setReservations] = useState<ReservationsDTO[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | AxiosError>(null);
  const [deleteError, setDeleteError] = useState<null | AxiosError>(null);
  const { logout } = useAuth();

  let load: () => void;
  load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchAuthenticatedFromBackend<ReservationsDTO[]>(`allUserReservationsForEvent/${id}`)
      .then((res) => {
        setReservations(res);
      })
      .catch((err) => {
        if (err.response && err.response.status === 401) {
          logout();
        } else {
          setError(err);
        }
      })
      .finally(() => setLoading(false));
  }, [id, logout]);

  useEffect(() => {
    load();
  }, [load]);

  const deleteReservation = useCallback(
    async (deleteId: string) => {
      setLoading(true);
      setDeleteError(null);

      try {
        await deleteRegistration(deleteId);
        load();
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          logout();
        } else {
          setDeleteError(error as AxiosError);
        }
      } finally {
        setLoading(false);
      }
    },
    [load, logout]
  );

  return { reservations, loading, fetchError: error, deleteError, reload: load, deleteReservation };
}
